import React, { useState } from 'react';
import { Container, Form, Button, Card, Alert } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import { auth, googleProvider, db } from '../firebase.ts';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import sprechheldenHeld from '../assets/sprechheldenHeld.png';

function LoginPage({ setIsAuthenticated, setHasSubscription }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Sign in with Firebase
      await signInWithEmailAndPassword(auth, email, password);
      
      // Login successful
      setIsAuthenticated(true);
      // For now, set subscription to true to bypass subscription page
      setHasSubscription(true);
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (error) {
      console.error('Login error:', error);
      
      // Handle specific Firebase errors
      switch (error.code) {
        case 'auth/user-not-found':
          setError('Benutzer nicht gefunden');
          break;
        case 'auth/wrong-password':
          setError('Falsches Passwort');
          break;
        case 'auth/invalid-email':
          setError('Ungültige E-Mail-Adresse');
          break;
        case 'auth/user-disabled':
          setError('Benutzerkonto wurde deaktiviert');
          break;
        default:
          setError('Anmeldung fehlgeschlagen. Bitte versuche es erneut.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setLoading(true);

    try {
      // Sign in with Google
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if user document exists
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      // If user document doesn't exist, create it
      if (!userDoc.exists()) {
        await setDoc(doc(db, 'users', user.uid), {
          id: user.uid,
          email: user.email,
          displayName: user.displayName || 'Google User',
          subscription: {
            type: 'none',
            name: 'Kein Abonnement',
            pricePerMonth: 0,
            features: [],
            billingPeriod: 'monthly',
            isActive: false
          },
          role: 'family',
          createdAt: new Date(),
          settings: {
            language: 'de',
            notifications: true
          },
          familyMembers: [],
          hasSeenWelcomeModal: false
        });

        // Create Stripe customer for new Google user
        try {
          const idToken = await user.getIdToken();
          const functionRegion = process.env.REACT_APP_FUNCTIONS_REGION || 'europe-west3';
          const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
          const response = await fetch(
            `https://${functionRegion}-${projectId}.cloudfunctions.net/createStripeCustomer`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${idToken}`
              },
              body: JSON.stringify({
                displayName: user.displayName || 'Google User',
                email: user.email
              })
            }
          );

          if (response.ok) {
            const result = await response.json();
            
            // Update user document with Stripe customer ID
            await updateDoc(doc(db, 'users', user.uid), {
              stripeCustomerId: result.customerId
            });
          } else {
            console.warn('Failed to create Stripe customer for new Google user, but login succeeded');
          }
        } catch (stripeError) {
          console.warn('Error creating Stripe customer for new Google user:', stripeError);
          // Don't fail login if Stripe customer creation fails
        }
        
        // New Google user needs subscription
        setIsAuthenticated(true);
        setHasSubscription(false);
        navigate('/subscription');
      } else {
        // Existing user - check if they have a Stripe customer ID
        const userData = userDoc.data();
        
        // If existing user doesn't have a Stripe customer ID, create one
        if (!userData.stripeCustomerId) {
          try {
            const idToken = await user.getIdToken();
            const functionRegion = process.env.REACT_APP_FUNCTIONS_REGION || 'europe-west3';
            const projectId = process.env.REACT_APP_FIREBASE_PROJECT_ID;
            const response = await fetch(
              `https://${functionRegion}-${projectId}.cloudfunctions.net/createStripeCustomer`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({
                  displayName: userData.displayName || user.displayName || 'User',
                  email: user.email
                })
              }
            );

            if (response.ok) {
              const result = await response.json();
              
              // Update user document with Stripe customer ID
              await updateDoc(doc(db, 'users', user.uid), {
                stripeCustomerId: result.customerId
              });
            } else {
              console.warn('Failed to create Stripe customer for existing user, but login succeeded');
            }
          } catch (stripeError) {
            console.warn('Error creating Stripe customer for existing user:', stripeError);
            // Don't fail login if Stripe customer creation fails
          }
        }
        
        // Existing user - check subscription status
        setIsAuthenticated(true);
        setHasSubscription(userData.subscription?.isActive || false);
        navigate(userData.subscription?.isActive ? '/dashboard' : '/subscription');
      }
    } catch (error) {
      console.error('Google sign-in error:', error);
      console.error('Error code:', error.code);
      console.error('Error message:', error.message);

      switch (error.code) {
        case 'auth/popup-closed-by-user':
          setError('Anmeldung abgebrochen');
          break;
        case 'auth/cancelled-popup-request':
          setError('Anmeldung abgebrochen');
          break;
        case 'auth/unauthorized-domain':
          setError('Diese Domain ist nicht für Google-Anmeldung autorisiert. Bitte kontaktiere den Administrator.');
          break;
        default:
          setError(`Google-Anmeldung fehlgeschlagen. Bitte versuche es erneut. (Fehlercode: ${error.code || 'unbekannt'})`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="min-vh-100 d-flex align-items-center justify-content-center">
      <div className="login-container">
        <Card className="shadow-sm">
          <Card.Body className="px-5 py-4" style={{ borderRadius: 'var(--radius-md)' }}>
            <div className="d-flex align-items-center justify-content-center mb-4">
              <img 
                src={sprechheldenHeld} 
                alt="Sprechhelden Hero" 
                style={{ width: '60px', height: 'auto' }}
                className="login-hero-image me-3"
              />
              <h2 className="mb-0">Sprechhelden</h2>
            </div>
            <p className="text-center text-muted mb-4">
              Willkommen zurück! Bitte melde dich an.
            </p>
            
            {error && (
              <Alert variant="danger" className="mb-3">
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>E-Mail Adresse</Form.Label>
                <Form.Control
                  type="email"
                  placeholder="name@beispiel.de"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Passwort</Form.Label>
                <Form.Control
                  type="password"
                  placeholder="Dein Passwort"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                />
              </Form.Group>

              <div className="text-end mb-3">
                <Link to="/forgot-password" className="text-muted small text-decoration-none" style={{ fontSize: 'var(--fs-caption)' }}>
                  Passwort vergessen?
                </Link>
              </div>

              <Button 
                variant="primary" 
                type="submit" 
                className="w-100"
                style={{ backgroundColor: 'var(--primary-color)', border: 'none' }}
                disabled={loading}
              >
                {loading ? 'Anmelde...' : 'Anmelden'}
              </Button>
            </Form>

            <div className="d-flex align-items-center my-4">
              <hr className="flex-grow-1" />
              <span className="px-3 text-muted">oder</span>
              <hr className="flex-grow-1" />
            </div>

            <Button 
              variant="outline-dark" 
              className="w-100 d-flex align-items-center justify-content-center"
              onClick={handleGoogleSignIn}
              disabled={loading}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" className="me-2">
                <g fill="none" fillRule="evenodd">
                  <path d="M17.64 9.205c0-.639-.057-1.252-.164-1.841H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
                  <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
                  <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
                  <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
                </g>
              </svg>
              Mit Google anmelden
            </Button>

            <p className="text-center text-muted mt-4 mb-0">
              Noch kein Konto? <Link to="/register" className="btn btn-link p-0 text-decoration-none">Registrieren</Link>
            </p>
          </Card.Body>
        </Card>
      </div>
    </Container>
  );
}

export default LoginPage;